import { useCalcPnlChartCategoriesWithProducingLosses } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategoriesWithProducingLosses";
import { PnlCategoriesEnum, PnlCategoryLabelsEnum } from "types/enums";
import { useMemo } from "react";
import { useSelector } from "store/useSelector";
import { groupBy, uniqBy } from "lodash";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import { PNL_ORDER } from "constants/constants";

/**
 * Loss item details with PNL category
 */
export interface LossItemDetails {
  name: string;
  pnlCategory: PnlCategoriesEnum;
}

/**
 * Get all unique loss types grouped by PNL category across all visible processes
 */
export function useTopLosses(): {
  byCategory: { [key in PnlCategoriesEnum]?: LossItemDetails[] };
  categories: PnlCategoriesEnum[];
} {
  const calcPnlChartCategories = useCalcPnlChartCategoriesWithProducingLosses();
  const dataDumpsByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const visibleProcesses = useVisibleProcesses();

  return useMemo(() => {
    // Initialize with empty results
    const byCategory: { [key in PnlCategoriesEnum]?: LossItemDetails[] } = {};

    // Return early if no data is available
    if (!visibleProcesses.length || !dataDumpsByProcess) {
      return {
        byCategory,
        categories: [],
      };
    }

    // Collect all losses from all visible processes
    const allLossItems: LossItemDetails[] = [];

    visibleProcesses.forEach(process => {
      const dataDumpWithProducingLosses = dataDumpsByProcess[process.id];
      if (!dataDumpWithProducingLosses) {
        return;
      }

      const pnlCategories = calcPnlChartCategories(dataDumpWithProducingLosses);

      // Process each category except producing
      pnlCategories.forEach(category => {
        if (
          category.title !== PnlCategoryLabelsEnum.producing &&
          category.bars
        ) {
          // Add each bar as a loss item
          category.bars.forEach(bar => {
            if (bar.title && bar.category) {
              allLossItems.push({
                name: bar.title,
                pnlCategory: bar.category as PnlCategoriesEnum,
              });
            }
          });
        }
      });
    });

    // Deduplicate loss items
    const uniqueLossItems = uniqBy(
      allLossItems,
      item => `${item.name}-${item.pnlCategory}`
    );

    // Group by PNL category
    const groupedByCategory = groupBy(uniqueLossItems, "pnlCategory");

    // Sort items within each category
    Object.keys(groupedByCategory).forEach(category => {
      groupedByCategory[category] = groupedByCategory[category].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    });

    // Get unique categories and sort by PNL_ORDER
    const categories = Object.keys(groupedByCategory) as PnlCategoriesEnum[];
    categories.sort((a, b) => (PNL_ORDER[a] || 999) - (PNL_ORDER[b] || 999));

    // filter out categories "Unknown" and "Uncollected"
    const filteredCategories = categories.filter(
      category =>
        category !== PnlCategoryLabelsEnum.unknown.toLowerCase() &&
        category !== PnlCategoryLabelsEnum.uncollected.toLowerCase()
    );

    return {
      byCategory: groupedByCategory,
      categories: filteredCategories,
    };
  }, [visibleProcesses, dataDumpsByProcess, calcPnlChartCategories]);
}
